import {
    DEFAULT_PAGE_COUNT,
    ratingColors,
    gradeColors,
    borderRatingColors,
    ratingText,
    BORDER_START,
    BORDER_ROOT,
    BORDER_SCENE,
    BORDER_WIDTH,
    badge,
    wrap
  } from './Constants';
	import { METRIC_KEYS } from './Constants';
	import { bandOf, BAND_FILL } from './verdictModel';
  import { actCodeFromNumber, actLabelFromNumber, unitSortKey } from './unitLabels';


  // Normalize page counts wherever scenes live (acts→seq→scene, top-level
  // sequences for TV, or top-level scenes for shorts).
  function ensurePageCounts(scenes) {
    (scenes || []).forEach(scene => {
      scene.page_count = scene.page_count ? parseFloat(scene.page_count) : DEFAULT_PAGE_COUNT;
    });
  }
  function preprocessScenes(ratings) {
    (ratings.acts || []).forEach(act => {
      (act.sequences || []).forEach(seq => { if (!seq.scenes) seq.scenes = []; ensurePageCounts(seq.scenes); });
    });
    (ratings.sequences || []).forEach(seq => { if (!seq.scenes) seq.scenes = []; ensurePageCounts(seq.scenes); });
    ensurePageCounts(ratings.scenes || []);
  }

	  function formatLabel(node) {
	    let labelText = node.wheelLabel || node.label;
	    if (node.nodeType === 'act') {
	      labelText = node.wheelLabel || `Act ${node.label}`;
	    } else if (node.nodeType === 'sequence') {
	      labelText = node.wheelLabel || `Seq ${node.label}`;
	    } else if (node.nodeType === 'scene') {
	      // NBSP padding ( ) on both sides so the design-band overlay frame doesn't
	      // clip the scene glyphs (see history). Symmetric → centering unchanged.
      labelText = `  sc ${node.label}  `;
    }
    return labelText;
  }

	  const ACT_SORT = { 1: 1, 2: 2, 3: 3, 4: 4 };

    function snakeOrCamel(obj, snake, camel) {
      return obj?.[snake] ?? obj?.[camel];
    }

    function labelSuffix(title) {
      const t = String(title || '').trim();
      return t ? `: ${t}` : '';
    }

    function sceneIdentity(scene, sceneId) {
      const number = Number(scene.number);
      const base = `Scene ${scene.number}`;
      return {
        id: scene.id || scene.unit_id || sceneId,
        unitId: scene.unit_id || scene.id || sceneId,
        displayLabel: snakeOrCamel(scene, 'display_label', 'displayLabel') || base,
        shortLabel: snakeOrCamel(scene, 'short_label', 'shortLabel') || base,
        listLabel: snakeOrCamel(scene, 'list_label', 'listLabel') || base,
        wheelLabel: snakeOrCamel(scene, 'wheel_label', 'wheelLabel') || base,
        sortKey: Number(snakeOrCamel(scene, 'sort_key', 'sortKey')) || number || 0,
        actCode: snakeOrCamel(scene, 'act_code', 'actCode') || null,
        actLabel: snakeOrCamel(scene, 'act_label', 'actLabel') || null,
      };
    }

    function sequenceIdentity(seq, label, actNumber, sortKey) {
      const seqNum = Number(seq.sequence_number ?? seq.number);
      const actCode = snakeOrCamel(seq, 'act_code', 'actCode') || (actNumber ? actCodeFromNumber(actNumber) : null);
      const actLabel = snakeOrCamel(seq, 'act_label', 'actLabel') || (actNumber ? actLabelFromNumber(actNumber) : null);
      const code = snakeOrCamel(seq, 'sequence_code', 'sequenceCode') || seq.code || label || (actCode ? `${actCode}-${seqNum}` : String(seqNum || ''));
      const shortLabel = snakeOrCamel(seq, 'short_label', 'shortLabel') || (seqNum ? `Seq ${seqNum}` : 'Seq');
      const listLabel = snakeOrCamel(seq, 'list_label', 'listLabel') || (actLabel ? `${actLabel}, ${shortLabel}` : shortLabel);
      const wheelLabel = snakeOrCamel(seq, 'wheel_label', 'wheelLabel') || (actCode ? `Seq ${code}` : shortLabel);
      const title = seq.title && String(seq.title).trim() !== String(seq.display_label || seq.displayLabel || '').trim() ? seq.title : '';
      const displayLabel = snakeOrCamel(seq, 'display_label', 'displayLabel') || `${listLabel}${labelSuffix(title)}`;
      const id = seq.id || seq.unit_id || `seq-${code}`;
      return { id, unitId: seq.unit_id || id, code, seqNum, actCode, actLabel, shortLabel, listLabel, wheelLabel, displayLabel, title, sortKey };
    }

    function actIdentity(act) {
      const num = Number(act.number);
      const code = snakeOrCamel(act, 'act_code', 'actCode') || act.code || actCodeFromNumber(num);
      const label = snakeOrCamel(act, 'act_label', 'actLabel') || actLabelFromNumber(num);
      const id = act.id || act.unit_id || `act-${code}`;
      return {
        id,
        unitId: act.unit_id || id,
        code,
        label,
        displayLabel: snakeOrCamel(act, 'display_label', 'displayLabel') || label,
        shortLabel: snakeOrCamel(act, 'short_label', 'shortLabel') || label,
        listLabel: snakeOrCamel(act, 'list_label', 'listLabel') || label,
        wheelLabel: snakeOrCamel(act, 'wheel_label', 'wheelLabel') || label,
        sortKey: Number(snakeOrCamel(act, 'sort_key', 'sortKey')) || ACT_SORT[num] || num,
      };
    }

  function prepareChartData(ratings, includeScenes = true, metric = 'overall', dimNotOwed = true) {
    // Overall colours by the gated band; other metrics use the gradeColors gradient.
    function fillFor(obj) {
      if (metric === 'overall') {
        const b = bandOf({ design: obj.design, execution: obj.execution, read: obj.read });
        if (b) return BAND_FILL[b];
      }
      return null;
    }

    // Neutral fill for a unit where the selected axis is NOT OWED: the system has no claim here, so
    // we must not paint it as a real score. Quiet slate, clearly "n/a".
    const NOT_OWED_FILL = '#eef2f5';
    // A unit "owes" an axis when the axis is genuinely at stake there. Two ways it may NOT be:
    //   • status says so outright (DER `st`: 'not_owed'/'na'), or
    //   • obligation is only 'light' — the axis is lightly relevant, not really expected here.
    // In both cases we must NOT paint a score colour (a low 'light' score isn't a real problem).
    // Lens aggregates (design/execution/read) carry neither field and are always owed.
    const NOT_OWED_STATUS = new Set(['not_owed', 'na', 'not_relevant', 'not_applicable']);
    const WEAK_OBLIGATION = new Set(['light', 'none', 'na', 'not_owed']);
    const cellOwed = (cell) => !!(cell && cell.score != null
      && !NOT_OWED_STATUS.has(cell.status)
      && !WEAK_OBLIGATION.has(cell.obligation));
    // gradeColors index from a score, WITHOUT the old `|| 5` trick that silently mapped a real 0
    // (worst) to 5 (mid). Only fall back to mid when the score is genuinely missing/NaN.
    function gradeColor(score) {
      const i = Math.round(Number(score));
      return Number.isFinite(i) ? (gradeColors[Math.max(0, Math.min(10, i))] || gradeColors[5]) : gradeColors[5];
    }
    // Final fill for any grain under the selected metric. For an axis metric, a not-owed unit gets
    // the neutral fill; otherwise the gradient. Overall/DE keep the gated band → execution gradient.
    function fillForGrain(obj, nodeType) {
      if (metric === 'overall' || metric === 'de') {
        return fillFor(obj) || gradeColor(parseFloat(obj.analysis?.overallScore ?? obj.execution ?? NaN));
      }
      const cell = obj.scores?.[METRIC_KEYS[metric]?.[nodeType]];
      // when the writer turns dimming off, paint not-owed units by their (faint) score like before
      if (!dimNotOwed) return gradeColor(parseFloat(cell?.score ?? NaN));
      return cellOwed(cell) ? gradeColor(parseFloat(cell.score)) : NOT_OWED_FILL;
    }
    // Is the selected axis not-owed for this unit? (used to quiet the border too)
    function notOwedHere(obj, nodeType) {
      if (metric === 'overall' || metric === 'de' || !dimNotOwed) return false;
      return !cellOwed(obj.scores?.[METRIC_KEYS[metric]?.[nodeType]]);
    }

    // ── shared node builders (used by every grain depth so a scene looks the
    // same whether it hangs off a sequence, or directly off the script) ──────
	    function sceneNodeObj(scene, parentId, sceneId) {
	      const pageCount = scene.page_count || DEFAULT_PAGE_COUNT;
	      const sceneColor = fillForGrain(scene, 'scene');
	      const sceneNotOwed = notOwedHere(scene, 'scene');
	      const ident = sceneIdentity(scene, sceneId);
	      const sortKey = ident.sortKey;
	      return {
	        id: ident.id,
	        parent: parentId,
	        label: `${scene.number}`,
          wheelLabel: ident.wheelLabel,
          listLabel: ident.listLabel,
	        sortKey,
	        value: pageCount,
        color: sceneColor,
        border: sceneNotOwed ? '#dde5ea' : BORDER_SCENE,
        notOwed: sceneNotOwed,
        nodeType: 'scene',
        detailedContent: {
          title: scene.title || `Scene ${scene.number}`,
          execution: scene.execution,
          design: scene.design ?? null,
          read: scene.read ?? null,
          headline: scene.headline ?? null,
          repair: scene.repair ?? null,
          synthesis: scene.synthesis ?? null,
          content_summary: scene.content_summary ?? null,   // Layered Read "what happens" (der_unit_summaries)
          mechanism: scene.mechanism ?? null,
          breadth: scene.breadth ?? null,
          prose: scene.prose ?? null,
          page: scene.page ?? null,
          overall: scene.overall ?? null,
          design_execution: scene.design_execution ?? null,
          execution_summary: scene.execution_executive_summary,
          page_count: pageCount,
	          type: 'scene',
	          number: scene.number,
            id: ident.id,
            unitId: ident.unitId,
            unit_id: ident.unitId,
            displayLabel: ident.displayLabel,
            display_label: ident.displayLabel,
            shortLabel: ident.shortLabel,
            short_label: ident.shortLabel,
            listLabel: ident.listLabel,
            list_label: ident.listLabel,
            wheelLabel: ident.wheelLabel,
            wheel_label: ident.wheelLabel,
            actCode: ident.actCode,
            act_code: ident.actCode,
            actLabel: ident.actLabel,
            act_label: ident.actLabel,
	          sortKey,
            sort_key: sortKey,
	          scores: scene.scores,
          text: scene.text,
          experts_critique: scene.experts_critique,
          critique: scene.critique,
          summary: scene.summary,
          experts_suggestions: scene.experts_suggestions,
        },
      };
    }

	    function sequenceNodeObj(seq, parentId, seqId, label, actNumber, sortKey) {
	      const seqFillColor = fillForGrain(seq, 'sequence');
	      const seqBorderColor = notOwedHere(seq, 'sequence') ? '#dde5ea' : (borderRatingColors[parseInt(seq.design || 0)] || '#999');
	      const ident = sequenceIdentity(seq, label, actNumber, sortKey);
	      return {
	        id: ident.id,
	        parent: parentId,
	        label: ident.code,
          wheelLabel: ident.wheelLabel,
          listLabel: ident.listLabel,
	        sortKey: ident.sortKey,
	        value: 0, // set from scenes
        color: seqFillColor,
        border: seqBorderColor,
        actNumber: actNumber ?? null,
        nodeType: 'sequence',
        detailedContent: {
	          title: ident.title,
	          // Child scene texts (number · title · text), so the sequence pane can offer a "Read the
	          // scenes" view — the whole stretch in order. Scenes are emitted as separate nodes, so this
	          // is the only place the sequence carries its children's raw text.
	          sceneTexts: Array.isArray(seq.scenes)
	            ? seq.scenes.map(s => ({
	                number: s.number,
	                label: (s.number != null ? `Scene ${s.number}` : '') + (s.title ? ` — ${s.title}` : ''),
	                text: (s.text || '').trim(),
	              })).filter(x => x.text)
	            : null,
	          analysis: seq.analysis,
	          id: ident.id,
            unitId: ident.unitId,
            unit_id: ident.unitId,
	          scores: seq.scores,
          summary: seq.summary,
          description: seq.design_executive_summary,
          design: seq.design,
          read: seq.read ?? null,
          headline: seq.headline ?? null,
          repair: seq.repair ?? null,
          synthesis: seq.synthesis ?? null,
          unit_title: seq.unit_title ?? null,          // Layered Read orientation title (der_unit_summaries)
          content_summary: seq.content_summary ?? null, // Layered Read "what happens" summary
          mechanism: seq.mechanism ?? null,
          breadth: seq.breadth ?? null,
          prose: seq.prose ?? null,
          page: seq.page ?? null,
          page_end: seq.page_end ?? null,
          execution: seq.execution,
          design_summary: seq.design_executive_summary,
	          execution_summary: seq.execution_executive_summary,
	          type: 'sequence',
	          number: ident.seqNum || seq.number,
            sequenceNumber: ident.seqNum || seq.number,
            sequence_number: ident.seqNum || seq.number,
	          code: ident.code,
            sequenceCode: ident.code,
            sequence_code: ident.code,
            actCode: ident.actCode,
            act_code: ident.actCode,
            actLabel: ident.actLabel,
            act_label: ident.actLabel,
            displayLabel: ident.displayLabel,
            display_label: ident.displayLabel,
            shortLabel: ident.shortLabel,
            short_label: ident.shortLabel,
            listLabel: ident.listLabel,
            list_label: ident.listLabel,
            wheelLabel: ident.wheelLabel,
            wheel_label: ident.wheelLabel,
	          sortKey: ident.sortKey,
            sort_key: ident.sortKey,
	        },
	      };
	    }

    // Emit a sequence + its scenes; returns the value (sum of scene page-counts).
    function emitSequence(nodes, seq, parentId, seqId, label, actNumber, sortKey, withScenes) {
      const seqNode = sequenceNodeObj(seq, parentId, seqId, label, actNumber, sortKey);
      nodes.push(seqNode);
	      let seqValue = 0;
	      if (withScenes && seq.scenes && seq.scenes.length > 0) {
	        [...seq.scenes].reverse().forEach(scene => {
	          const sceneId = scene.id || scene.unit_id || `scene-${scene.number}`;
	          const sceneNode = sceneNodeObj(scene, seqNode.id, sceneId);
	          if (seenSceneIds.has(sceneNode.id)) return;
	          seenSceneIds.add(sceneNode.id);
	          nodes.push(sceneNode);
          seqValue += sceneNode.value;
        });
      }
      if (seqValue === 0) seqValue = 1;
      seqNode.value = seqValue;
      return seqValue;
    }

    preprocessScenes(ratings);

    // ── what grains exist? drives ring depth and where scenes/sequences attach ──
    const hasActs = Array.isArray(ratings.acts) && ratings.acts.length > 0;
    // Sequences the builder could not hang under an act. AnalysisController::buildRatingsFromDer
    // surfaces these at the top level ("sequences with no act parent"), which until now only
    // happened for TV — a format with no acts at all.
    const looseSequences = (Array.isArray(ratings.sequences) && ratings.sequences.length > 0)
      ? ratings.sequences : null;
    const topSequences = (!hasActs) ? looseSequences : null;
    // ⚠️ A SEQUENCE OUTSIDE EVERY ACT USED TO RENDER AS NOTHING, and that silence is why a stage
    // PROLOGUE is currently filed under Act I against the playwright's own intent — The Threepenny
    // Opera's calling sheet lists it as a peer of the acts ("Prologue; Act I, Scene 3; Act II,
    // Scene 2"), and we had to choose between misfiled and invisible. `topSequences` was gated on
    // !hasActs, so with acts present this array was dropped on the floor: no node, no warning, the
    // beats simply absent from the read.
    //
    // ⚠️ PARENTED TO THE ROOT, NOT TO AN ACT, WHICH IS THE POINT. A prologue sits BESIDE the
    // acts, so it takes an act-ring slot and its beats take the sequence ring. That is one ring
    // shallower than a scene inside an act — deliberately. The alternative is inventing a
    // containing act, which is the fiction this removes.
    const orphanSequences = hasActs ? looseSequences : null;
    const topScenes = (!hasActs && !topSequences && Array.isArray(ratings.scenes) && ratings.scenes.length > 0)
      ? ratings.scenes : null;
    // shorts (scenes only) always show their one scene ring regardless of the toggle
    const showScenes = topScenes ? true : includeScenes;
    let depth;
    if (hasActs) depth = showScenes ? 3 : 2;
    else if (topSequences) depth = showScenes ? 2 : 1;
    else depth = 1;

    const nodes = [];
    // DEFENSIVE: a scene id must render exactly once. Bad data can assign one scene to two
    // sequences (overlapping from/to ranges) — without this the duplicate cell overflows the
    // scenes row. Root-cause fix tracked separately (overlapping sequence ranges).
    const seenSceneIds = new Set();

    // Root
    nodes.push({
      id: 'Movie', parent: '', label: 'Script', value: 0, color: '#eee', border: BORDER_ROOT,
      nodeType: 'movie',
      detailedContent: {
        title: ratings.movie.title,
        description: 'Complete analysis of the entire movie.',
        type: 'movie',
        pcr: ratings.movie.pcr,
        execution: ratings.movie.execution,
        design: ratings.movie.design,
        read: ratings.movie.read ?? null,
        scores: ratings.movie.scores ?? null,
        headline: ratings.movie.headline ?? null,
        repair: ratings.movie.repair ?? null,
        synthesis: ratings.movie.synthesis ?? null,
        mechanism: ratings.movie.mechanism ?? null,
        breadth: ratings.movie.breadth ?? null,
        prose: ratings.movie.prose ?? null,
        number: null,
        execution_summary: 'Overall execution score for the movie.',
        design_summary: 'Overall design score for the movie.',
      },
    });

    // Gap / START spacer chain — exactly `depth` rings deep so it spans the
    // content rings without adding a phantom ring beyond them.
    const gapPercentage = 0.015;
    const gapIds = [];
    let gapParent = 'Movie';
    for (let d = 0; d < depth; d++) {
      const id = d === 0 ? 'gap' : `gap-${d}`;
      nodes.push({
        id, parent: gapParent, label: d === 0 ? 'START' : '→', value: 1,
        color: '#000000', border: '#000000', nodeType: 'gap',
        detailedContent: d === 0
          ? { title: 'Starting Point', execution_summary: 'The screenplay begins here and flows clockwise.', type: 'gap' }
          : undefined,
      });
      gapIds.push(id);
      gapParent = id;
    }

    let totalContentValue = 0;

	    if (hasActs) {
	      // ── FEATURE: Script → Act → Sequence → Scene ──────────────────────────
	      const actsForWheel = [...ratings.acts].sort((a, b) => unitSortKey(a) - unitSortKey(b)).reverse(); // reverse for clockwise
	      actsForWheel.forEach(act => {
	        const num = Number(act.number);
	        if (!act) return;
	        const ident = actIdentity(act);
	        const code = ident.code;
	        const fillColor = fillForGrain(act, 'act');
	        const borderColor = notOwedHere(act, 'act') ? '#dde5ea' : (borderRatingColors[parseInt(act.design || 0)] || '#999');

	        const actNode = {
	          id: ident.id, parent: 'Movie', label: code, wheelLabel: ident.wheelLabel, listLabel: ident.listLabel, sortKey: ident.sortKey, value: 0, color: fillColor,
	          border: borderColor, actNumber: num, nodeType: 'act',
	          detailedContent: {
	            title: act.title, analysis: act.analysis, scores: act.scores, summary: act.summary,
	            id: ident.id, unitId: ident.unitId, unit_id: ident.unitId, description: act.design_executive_summary,
	            design: act.design ?? null, read: act.read ?? null, headline: act.headline ?? null,
	            repair: act.repair ?? null, synthesis: act.synthesis ?? null, mechanism: act.mechanism ?? null,
            unit_title: act.unit_title ?? null, content_summary: act.content_summary ?? null,   // Layered Read orientation
	            breadth: act.breadth ?? null, prose: act.prose ?? null, page: act.page ?? null,
	            page_end: act.page_end ?? null, execution: act.execution,
	            execution_summary: act.execution_executive_summary, type: 'act', number: num, code,
              actCode: code, act_code: code, actLabel: ident.label, act_label: ident.label,
              displayLabel: ident.displayLabel, display_label: ident.displayLabel,
              shortLabel: ident.shortLabel, short_label: ident.shortLabel,
              listLabel: ident.listLabel, list_label: ident.listLabel,
              wheelLabel: ident.wheelLabel, wheel_label: ident.wheelLabel,
              sortKey: ident.sortKey, sort_key: ident.sortKey,
	          },
	        };
	        nodes.push(actNode);
	        let actTotal = 0;
	        [...(act.sequences || [])].sort((a, b) => unitSortKey(a) - unitSortKey(b)).reverse().forEach(seq => {
	          const seqCode = seq.sequence_code || seq.sequenceCode || `${code}-${seq.number}`;
	          const seqSortKey = unitSortKey(seq) || ((ACT_SORT[num] || 99) * 1000) + (Number(seq.number) || 0);
	          actTotal += emitSequence(nodes, seq, ident.id, seq.id || seq.unit_id || `seq-${seqCode}`, seqCode, num, seqSortKey, showScenes);
	        });
        actNode.value = actTotal;
        totalContentValue += actTotal;
      });
      // A prologue / entr'acte / epilogue that belongs to no act, emitted beside them.
      if (orphanSequences) {
        [...orphanSequences].sort((a, b) => unitSortKey(a) - unitSortKey(b)).reverse().forEach(seq => {
          const seqCode = seq.sequence_code || seq.sequenceCode || `${seq.number}`;
          const seqSortKey = unitSortKey(seq) || Number(seq.number) || 0;
          totalContentValue += emitSequence(nodes, seq, 'Movie',
            seq.id || seq.unit_id || `seq-${seqCode}`, `${seqCode}`, null, seqSortKey, showScenes);
        });
      }
	    } else if (topSequences) {
	      // ── TV: Script/Episode → Sequence → Scene (no act ring) ───────────────
	      [...topSequences].sort((a, b) => unitSortKey(a) - unitSortKey(b)).reverse().forEach(seq => {
	        const seqSortKey = unitSortKey(seq) || Number(seq.number) || 0;
          const seqCode = seq.sequence_code || seq.sequenceCode || `${seq.number}`;
	        totalContentValue += emitSequence(nodes, seq, 'Movie', seq.id || seq.unit_id || `seq-${seqCode}`, `${seqCode}`, null, seqSortKey, showScenes);
	      });
	    } else if (topScenes) {
	      // ── SHORT: Script → Scene (no act, no sequence ring) ──────────────────
	      [...topScenes].sort((a, b) => unitSortKey(a) - unitSortKey(b)).reverse().forEach(scene => {
	        const node = sceneNodeObj(scene, 'Movie', scene.id || scene.unit_id || `scene-${scene.number}`);
	        if (seenSceneIds.has(node.id)) return;
	        seenSceneIds.add(node.id);
	        nodes.push(node);
	        totalContentValue += node.value;
	      });
    }

    // Gap sizing
    const gapValue = totalContentValue > 0 ? (totalContentValue * gapPercentage / (1 - gapPercentage)) : 1;
    gapIds.forEach(id => { const g = nodes.find(n => n.id === id); if (g) g.value = gapValue; });
    const totalValue = totalContentValue + gapValue;

    // Root value + colour (the script's own verdict drives the centre)
    nodes[0].value = totalValue;
    const m = ratings.movie || {};
    const rootBand = bandOf({ design: m.design, execution: m.execution, read: m.read });
    if (metric === 'overall' && rootBand) {
      nodes[0].color = BAND_FILL[rootBand];
    } else if (m.execution != null) {
      nodes[0].color = gradeColors[Math.round(parseFloat(m.execution)) || 5] || '#eee';
    }
    if (m.design != null) nodes[0].border = gradeColors[Math.round(parseFloat(m.design)) || 5] || BORDER_ROOT;
    nodes[0].detailedContent.design = m.design;
    nodes[0].detailedContent.execution = m.execution;

    nodes.forEach(node => {
      if (node.nodeType === 'gap') {
        node.formattedLabel = node.id === 'gap' ? 'START' : '→';
      } else {
        node.formattedLabel = formatLabel(node);
      }
    });

    return { nodes };
  }

  export { prepareChartData };
